import { useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  useTasks,
  useCreateTask,
  useUpdateTask,
} from '~/app/hooks/maintenance/useTasks';
import { useWorkTypes } from '~/app/hooks/maintenance/useWorkTypes';
import { useParts } from '~/app/hooks/maintenance/useParts';
import { toSubtaskPayload } from '~/app/components/maintenance/TaskSubtasksField/TaskSubtasksField';
import type { TaskResponseDto } from '@evimo/client/models';
import { useTasksTabColumns } from '../../../hooks/useTasksTabColumns';

export function useTasksTab() {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const { data: tasks = [], isLoading: tasksLoading } = useTasks();
  const { data: workTypes = [] } = useWorkTypes();
  const { parts } = useParts();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskResponseDto | null>(null);
  const [form] = Form.useForm();

  const workTypeMap = useMemo(
    () => new Map(workTypes.map((wt) => [wt.id, wt.code])),
    [workTypes],
  );

  const partNameById = useMemo(
    () => new Map(parts.map((part) => [part.id, part.name])),
    [parts],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingTask(null);
    form.resetFields();
  }, [form]);

  const openCreate = useCallback(() => {
    setEditingTask(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback(
    (task: TaskResponseDto) => {
      setEditingTask(task);
      form.setFieldsValue({
        name: task.name,
        workTypeId: task.workTypeId,
        mandatory: task.mandatory,
        requiresShutdown: task.requiresShutdown,
        estimatedDurationMinutes: task.estimatedDurationMinutes ?? undefined,
        partIds: task.partIds ?? [],
        subtasks: (task.subtasks ?? []).map((subtask) => ({
          name: subtask.name,
        })),
      });
      setModalOpen(true);
    },
    [form],
  );

  const { columns, expandable } = useTasksTabColumns({
    workTypeMap,
    partNameById,
    isSuperAdmin,
    organizationId: user?.organizationId,
    onEdit: openEdit,
  });

  const handleSubmit = useCallback(async () => {
    const values = await form.validateFields();
    const body = {
      name: values.name as string,
      workTypeId: values.workTypeId as string,
      mandatory: values.mandatory as boolean | undefined,
      requiresShutdown: values.requiresShutdown as boolean | undefined,
      estimatedDurationMinutes:
        (values.estimatedDurationMinutes as number | null | undefined) ?? null,
      partIds: (values.partIds as string[] | undefined) ?? [],
      subtasks: toSubtaskPayload(values.subtasks),
    };
    if (editingTask) {
      await updateTaskMutation.mutateAsync({ id: editingTask.id, body });
    } else {
      await createTaskMutation.mutateAsync(body);
    }
    closeModal();
  }, [closeModal, createTaskMutation, editingTask, form, updateTaskMutation]);

  const workTypeOptions = useMemo(
    () =>
      workTypes.map((wt) => ({
        label: wt.code,
        value: wt.id,
        code: wt.code,
      })),
    [workTypes],
  );

  const partOptions = useMemo(
    () => parts.map((p) => ({ label: p.name, value: p.id })),
    [parts],
  );

  return {
    tasks,
    tasksLoading,
    columns,
    expandable,
    modalOpen,
    editingTask,
    form,
    closeModal,
    openCreate,
    handleSubmit,
    isSaving: createTaskMutation.isPending || updateTaskMutation.isPending,
    workTypeOptions,
    partOptions,
  };
}
